'use client'
import React, { useEffect, useState } from 'react'
import RichText from '../../components/RichText'
import { Icon } from '../Icon'
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import { ButtonField } from '@/payload-types'
import { CMSLink } from '@/ui/components/CMSLink'
import { cn } from '@/utils/ui'
import { EmptyState } from '../../components/EmptyStates'
import type { EmptyStateType } from '../../components/EmptyStates/types'
import { sendGAEvent } from '@next/third-parties/google'
import { trackEvent } from '@/lib/analytics/posthog'

export interface AccordionAnalyticsContext {
  accordion_type?: 'technical_params' | 'service_support' | 'core_questions' | 'regulations'
  page_type?: string
}

export interface AccordionProps {
  items: {
    title?: string
    icon?: string
    content?: SerializedEditorState | null
    button?: ButtonField
  }[]
  hasNumeration?: boolean
  appearance?: 'filled' | 'outlined'
  openFirstByDefault?: boolean
  emptyStateText?: string
  emptyStateType?: EmptyStateType
  emptyStateButton?: ButtonField
  emptyStateHasButton?: boolean
  analyticsContext?: AccordionAnalyticsContext
}

const Accordion: React.FC<AccordionProps> = ({
  items,
  hasNumeration,
  appearance = 'filled',
  openFirstByDefault = false,
  emptyStateText,
  emptyStateType = 'faqs',
  emptyStateButton,
  emptyStateHasButton = false,
  analyticsContext,
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(openFirstByDefault ? 0 : null)

  useEffect(() => {
    setOpenIndex(openFirstByDefault ? 0 : null)
  }, [items])

  const handleClick = (index: number) => {
    if (openIndex !== index) {
      const label = items[index]?.title || 'Accordion Item'
      sendGAEvent('event', 'accordionItemClicked', { label })
      trackEvent('accordionItemClicked', { label, ...analyticsContext })
    }
    setOpenIndex(openIndex === index ? null : index)
  }

  if (!items || items.length === 0) {
    console.log('Accordion empty state props:', {
      emptyStateButton,
      emptyStateHasButton,
      emptyStateType,
      emptyStateText,
    })
    return (
      <EmptyState
        type={emptyStateType}
        button={emptyStateButton}
        hasButton={emptyStateHasButton}
        className="w-full"
      />
    )
  }

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div
          key={index}
          className={cn('rounded-md', {
            'bg-bg-subtle': appearance === 'filled',
            'bg-bg-base border border-border-subtle': appearance === 'outlined',
          })}
        >
          <button
            className={cn(
              `w-full flex justify-between items-center !text-body-M-semibold text-black hover:bg-neutral-95 transition-all duration-200 ease-in-out p-card-s rounded-md border-2 border-solid border-transparent focus:outline-none focus:border-border-focus`,
              { 'bg-neutral-95': openIndex === index, 'bg-bg-base': appearance == 'outlined' },
            )}
            onClick={() => handleClick(index)}
          >
            <div className="flex gap-3 items-center text-start">
              {item.icon && <Icon name={item.icon} useGradient />}
              {hasNumeration && (
                <span className="rounded-sm bg-soft-gradient-blue min-w-4 size-4 text-white text-label-XS-semibold flex items-center justify-center">
                  {index + 1}
                </span>
              )}
              {item.title}
            </div>
            <Icon name={openIndex === index ? 'arrow-up' : 'arrow-down'} className={`h-6 w-6`} />
          </button>
          {openIndex === index && (
            <div className="p-grid-gutter">
              {item.content && (
                <RichText
                  enableGutter={false}
                  enableProse={true}
                  data={item.content}
                  className="!text-body-M-regular text-text-base"
                />
              )}
              {item.button && (
                <div className="mt-6">
                  <CMSLink {...item.button} isSpecialLink className={`flex items-center`} />
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default Accordion
